import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Trash2, Save, Eye, FileDown, KeyRound, Mail } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export function AdminUsersTab({ users, fetchData }) {
  const [newUser, setNewUser] = useState({ email: '', password: '', nome_cognome: '', societa_associazione: '', indirizzo: '', casella_postale: '', comune_residenza: '', codice_fiscale: '', partita_iva: '', tipo_richiedente: '', tipo_soggetto: '', codice_fattura_digitale: '' });
  const [viewingUser, setViewingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({});
  const [savingUser, setSavingUser] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceMode, setInvoiceMode] = useState('mensile');
  const [invoicePeriod, setInvoicePeriod] = useState('');

  const handleAdminRegisterUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.nome_cognome || !newUser.tipo_soggetto || !newUser.tipo_richiedente) {
      toast.error('Compila tutti i campi obbligatori (Email, Password, Nome, Tipo Soggetto, Tipo Richiedente)');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/register-user`, newUser);
      toast.success('Utente registrato');
      setNewUser({ email: '', password: '', nome_cognome: '', societa_associazione: '', indirizzo: '', casella_postale: '', comune_residenza: '', codice_fiscale: '', partita_iva: '', tipo_richiedente: '', tipo_soggetto: '', codice_fattura_digitale: '' });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Errore registrazione'); }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API_URL}/users/${userId}`);
      toast.success('Utente cancellato');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Errore nella cancellazione'); }
  };

  const handleToggleDachverband = async (userId, currentValue) => {
    const newValue = currentValue === true ? false : currentValue === false ? null : true;
    try {
      const res = await axios.put(`${API_URL}/users/${userId}/dachverband`, { is_dachverband: newValue });
      const msg = res.data.recalculated > 0
        ? `Stato Dachverband aggiornato. ${res.data.recalculated} prenotazioni ricalcolate.`
        : 'Stato Dachverband aggiornato';
      toast.success(msg);
      fetchData();
    } catch (error) { toast.error('Errore aggiornamento'); }
  };

  const handleToggleInvoiceStatus = async (userId, field, value) => {
    try {
      await axios.put(`${API_URL}/users/${userId}`, { [field]: value });
      toast.success(field === 'fattura_emessa' ? (value ? 'Fattura marcata come emessa' : 'Fattura marcata come non emessa') : (value ? 'Fattura marcata come pagata' : 'Fattura marcata come non pagata'));
      fetchData();
    } catch (error) { toast.error('Errore aggiornamento'); }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUserId || newPassword.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }
    try {
      await axios.put(`${API_URL}/users/${resetPasswordUserId}/reset-password`, { new_password: newPassword });
      toast.success('Password reimpostata con successo');
      setResetPasswordUserId(null);
      setNewPassword('');
    } catch (error) { toast.error(error.response?.data?.detail || 'Errore nel reset password'); }
  };

  const handleViewUser = (user) => { setViewingUser(user); setEditUserData({ ...user }); };

  const handleSaveUser = async () => {
    if (!viewingUser) return;
    setSavingUser(true);
    try {
      await axios.put(`${API_URL}/users/${viewingUser.id}`, editUserData);
      toast.success('Dati utente aggiornati');
      setViewingUser(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Errore aggiornamento'); }
    finally { setSavingUser(false); }
  };

  const handleExportInvoice = async (userId) => {
    try {
      const res = await axios.get(`${API_URL}/users/${userId}/invoice`);
      setInvoiceData(res.data);
      const months = Object.keys(res.data.monthly_totals).sort();
      const years = Object.keys(res.data.yearly_totals).sort();
      if (invoiceMode === 'mensile' && months.length > 0) setInvoicePeriod(months[months.length - 1]);
      else if (years.length > 0) setInvoicePeriod(years[years.length - 1]);
    } catch (error) { toast.error('Errore caricamento fattura'); }
  };

  const getFilteredBookings = () => {
    if (!invoiceData || !invoicePeriod) return invoiceData?.bookings || [];
    return invoiceData.bookings.filter(b => {
      const sd = b.start_datetime || '';
      return sd.startsWith(invoicePeriod);
    });
  };

  const getFilteredTotal = () => {
    if (!invoiceData || !invoicePeriod) return 0;
    return invoiceMode === 'mensile' ? (invoiceData.monthly_totals[invoicePeriod] || 0) : (invoiceData.yearly_totals[invoicePeriod] || 0);
  };

  const generateInvoiceText = () => {
    if (!invoiceData) return '';
    const u = invoiceData.user;
    const filtered = getFilteredBookings();
    const total = getFilteredTotal();
    const periodLabel = invoiceMode === 'mensile' ? `Mese: ${invoicePeriod}` : `Anno: ${invoicePeriod}`;
    let text = `FATTURA / RIEPILOGO PRENOTAZIONI\n${'='.repeat(50)}\n${periodLabel}\n\nUTENTE:\nNome: ${u.nome_cognome || ''}\nEmail: ${u.email || ''}\n`;
    if (u.societa_associazione) text += `Societ\u00e0: ${u.societa_associazione}\n`;
    if (u.indirizzo) text += `Indirizzo: ${u.indirizzo}\n`;
    if (u.comune_residenza) text += `Comune: ${u.comune_residenza}\n`;
    if (u.codice_fiscale) text += `Cod. Fiscale: ${u.codice_fiscale}\n`;
    if (u.partita_iva) text += `P.IVA: ${u.partita_iva}\n`;
    if (u.codice_fattura_digitale) text += `Cod. Fattura Digitale (SDI): ${u.codice_fattura_digitale}\n`;
    text += `\n${'='.repeat(50)}\nPRENOTAZIONI:\n${'='.repeat(50)}\n\n`;
    filtered.forEach((b, i) => { text += `${i + 1}. ${b.hall_name}\n   ${b.start_datetime || ''} - ${b.end_datetime || ''}\n   ${b.descrizione_manifestazione || ''}\n   Totale: EUR ${(b.total_price || 0).toFixed(2)}\n\n`; });
    text += `${'='.repeat(50)}\nTOTALE ${invoiceMode === 'mensile' ? 'MENSILE' : 'ANNUALE'}: EUR ${total.toFixed(2)}\n`;
    return text;
  };

  return (
    <>
      {/* Register New User */}
      <Card className="mb-6">
        <CardHeader><CardTitle className="font-heading text-xl">Registra Nuovo Utente</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Input placeholder="Email *" type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} data-testid="admin-new-user-email" />
            <Input placeholder="Password *" type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} data-testid="admin-new-user-password" />
            <Input placeholder="Nome e Cognome *" value={newUser.nome_cognome} onChange={(e) => setNewUser({...newUser, nome_cognome: e.target.value})} data-testid="admin-new-user-name" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Tipo Soggetto *</label>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={newUser.tipo_soggetto} onChange={(e) => setNewUser({...newUser, tipo_soggetto: e.target.value})} data-testid="admin-new-user-tipo-soggetto">
                <option value="">Seleziona...</option>
                <option value="persona_fisica">Persona fisica</option>
                <option value="azienda">Azienda</option>
                <option value="associazione">Associazione</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Tipo Richiedente *</label>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={newUser.tipo_richiedente} onChange={(e) => setNewUser({...newUser, tipo_richiedente: e.target.value})} data-testid="admin-new-user-tipo-richiedente">
                <option value="">Seleziona...</option>
                <option value="1">Ass. pubbl. util. residenti</option>
                <option value="2">Ass. non res. / Pers. residenti</option>
                <option value="3">Non residenti</option>
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Input placeholder="Societ\u00e0/Associazione" value={newUser.societa_associazione} onChange={(e) => setNewUser({...newUser, societa_associazione: e.target.value})} data-testid="admin-new-user-societa" />
            <Input placeholder="Indirizzo" value={newUser.indirizzo} onChange={(e) => setNewUser({...newUser, indirizzo: e.target.value})} data-testid="admin-new-user-indirizzo" />
            <Input placeholder="Comune Residenza" value={newUser.comune_residenza} onChange={(e) => setNewUser({...newUser, comune_residenza: e.target.value})} data-testid="admin-new-user-comune" />
            <Input placeholder="Casella Postale" value={newUser.casella_postale} onChange={(e) => setNewUser({...newUser, casella_postale: e.target.value})} data-testid="admin-new-user-casella" />
            <Input placeholder="Codice Fiscale" value={newUser.codice_fiscale} onChange={(e) => setNewUser({...newUser, codice_fiscale: e.target.value})} data-testid="admin-new-user-cf" />
            <Input placeholder="Partita IVA" value={newUser.partita_iva} onChange={(e) => setNewUser({...newUser, partita_iva: e.target.value})} data-testid="admin-new-user-piva" />
            {newUser.tipo_soggetto === 'azienda' && (
              <Input placeholder="Codice Fattura Digitale (SDI)" value={newUser.codice_fattura_digitale} onChange={(e) => setNewUser({...newUser, codice_fattura_digitale: e.target.value})} data-testid="admin-new-user-sdi" />
            )}
          </div>
          <Button onClick={handleAdminRegisterUser} className="btn-scale" data-testid="admin-register-user-button">
            <Save className="w-4 h-4 mr-2" /> Registra Utente
          </Button>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-2xl">Gestione Utenti</CardTitle></CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nessun utente</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Societ\u00e0</TableHead><TableHead>Dachverband</TableHead><TableHead>Emessa</TableHead><TableHead>Pagata</TableHead><TableHead>Ruolo</TableHead><TableHead>Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} data-testid={`admin-user-${user.id}`}>
                      <TableCell className="font-medium">{user.nome_cognome}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.societa_associazione || '-'}</TableCell>
                      <TableCell>
                        {!user.is_admin ? (
                          <Button variant={user.is_dachverband === true ? "default" : user.is_dachverband === false ? "destructive" : "outline"} size="sm" onClick={() => handleToggleDachverband(user.id, user.is_dachverband)} data-testid={`dachverband-${user.id}`}>
                            {user.is_dachverband === true ? 'Socio' : user.is_dachverband === false ? 'Non socio' : 'Non impostato'}
                          </Button>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {!user.is_admin && (
                          <Button size="sm" variant={user.fattura_emessa ? "default" : "outline"} className={user.fattura_emessa ? "bg-green-600 hover:bg-green-700 text-white" : ""} onClick={() => handleToggleInvoiceStatus(user.id, 'fattura_emessa', !user.fattura_emessa)} data-testid={`fattura-emessa-${user.id}`}>
                            {user.fattura_emessa ? 'S\u00ec' : 'No'}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        {!user.is_admin && (
                          <Button size="sm" variant={user.fattura_pagata ? "default" : "outline"} className={user.fattura_pagata ? "bg-green-600 hover:bg-green-700 text-white" : ""} onClick={() => handleToggleInvoiceStatus(user.id, 'fattura_pagata', !user.fattura_pagata)} data-testid={`fattura-pagata-${user.id}`}>
                            {user.fattura_pagata ? 'S\u00ec' : 'No'}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>{user.is_admin ? <Badge variant="default">Admin</Badge> : <Badge variant="secondary">User</Badge>}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="outline" size="sm" onClick={() => handleViewUser(user)} data-testid={`view-user-${user.id}`}><Eye className="w-4 h-4" /></Button>
                          <Button variant="outline" size="sm" onClick={() => handleExportInvoice(user.id)} data-testid={`invoice-user-${user.id}`}><FileDown className="w-4 h-4" /></Button>
                          {!user.is_admin && (
                            <>
                              <Button variant="outline" size="sm" onClick={() => { setResetPasswordUserId(user.id); setNewPassword(''); }} data-testid={`reset-pwd-${user.id}`}><KeyRound className="w-4 h-4" /></Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild><Button variant="destructive" size="sm"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader><AlertDialogTitle>Conferma Cancellazione</AlertDialogTitle><AlertDialogDescription>Cancellare questo utente e tutte le sue prenotazioni?</AlertDialogDescription></AlertDialogHeader>
                                  <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteUser(user.id)}>Cancella</AlertDialogAction></AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordUserId} onOpenChange={(open) => { if (!open) { setResetPasswordUserId(null); setNewPassword(''); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reimposta Password</DialogTitle><DialogDescription>Inserisci la nuova password per l&apos;utente.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nuova Password (min. 6 caratteri)</Label>
              <Input id="new-password" type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Inserisci nuova password..." data-testid="new-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetPasswordUserId(null); setNewPassword(''); }}>Annulla</Button>
            <Button onClick={handleResetPassword} disabled={newPassword.length < 6} data-testid="confirm-reset-password"><KeyRound className="w-4 h-4 mr-2" /> Reimposta Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View/Edit User Dialog */}
      <Dialog open={!!viewingUser} onOpenChange={(open) => { if (!open) setViewingUser(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle>Dati Utente</DialogTitle><DialogDescription>Visualizza e modifica i dati dell&apos;utente</DialogDescription></DialogHeader>
          {viewingUser && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Nome e Cognome</Label><Input value={editUserData.nome_cognome || ''} onChange={(e) => setEditUserData({...editUserData, nome_cognome: e.target.value})} /></div>
                <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={editUserData.email || ''} onChange={(e) => setEditUserData({...editUserData, email: e.target.value})} /></div>
                <div className="space-y-1"><Label className="text-xs">Societ\u00e0/Associazione</Label><Input value={editUserData.societa_associazione || ''} onChange={(e) => setEditUserData({...editUserData, societa_associazione: e.target.value})} /></div>
                <div className="space-y-1"><Label className="text-xs">Indirizzo</Label><Input value={editUserData.indirizzo || ''} onChange={(e) => setEditUserData({...editUserData, indirizzo: e.target.value})} /></div>
                <div className="space-y-1"><Label className="text-xs">Casella Postale</Label><Input value={editUserData.casella_postale || ''} onChange={(e) => setEditUserData({...editUserData, casella_postale: e.target.value})} /></div>
                <div className="space-y-1"><Label className="text-xs">Comune Residenza</Label><Input value={editUserData.comune_residenza || ''} onChange={(e) => setEditUserData({...editUserData, comune_residenza: e.target.value})} /></div>
                <div className="space-y-1"><Label className="text-xs">Codice Fiscale</Label><Input value={editUserData.codice_fiscale || ''} onChange={(e) => setEditUserData({...editUserData, codice_fiscale: e.target.value})} /></div>
                <div className="space-y-1"><Label className="text-xs">Partita IVA</Label><Input value={editUserData.partita_iva || ''} onChange={(e) => setEditUserData({...editUserData, partita_iva: e.target.value})} /></div>
                <div className="space-y-1">
                  <Label className="text-xs">Tipo Richiedente</Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editUserData.tipo_richiedente || ''} onChange={(e) => setEditUserData({...editUserData, tipo_richiedente: e.target.value})}>
                    <option value="">Non impostato</option><option value="1">Ass. pubbl. util. residenti</option><option value="2">Ass. non res. / Pers. residenti</option><option value="3">Non residenti</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tipo Soggetto</Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editUserData.tipo_soggetto || ''} onChange={(e) => setEditUserData({...editUserData, tipo_soggetto: e.target.value})}>
                    <option value="">Non impostato</option><option value="persona_fisica">Persona fisica</option><option value="azienda">Azienda</option><option value="associazione">Associazione</option>
                  </select>
                </div>
              </div>
              {editUserData.tipo_soggetto === 'azienda' && (
                <div className="space-y-1"><Label className="text-xs">Codice Fattura Digitale (SDI)</Label><Input value={editUserData.codice_fattura_digitale || ''} onChange={(e) => setEditUserData({...editUserData, codice_fattura_digitale: e.target.value})} /></div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingUser(null)}>Chiudi</Button>
            <Button onClick={handleSaveUser} disabled={savingUser} data-testid="save-user-data"><Save className="w-4 h-4 mr-2" />{savingUser ? 'Salvataggio...' : 'Salva Modifiche'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={!!invoiceData} onOpenChange={(open) => { if (!open) setInvoiceData(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle>Fattura / Riepilogo Prenotazioni</DialogTitle><DialogDescription>{invoiceData?.user?.nome_cognome} - {invoiceData?.user?.email}</DialogDescription></DialogHeader>
          {invoiceData && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-3 rounded-lg border">
                <Label className="font-semibold text-sm">Tipo fattura:</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant={invoiceMode === 'mensile' ? 'default' : 'outline'} onClick={() => { setInvoiceMode('mensile'); const m = Object.keys(invoiceData.monthly_totals).sort(); if (m.length > 0) setInvoicePeriod(m[m.length - 1]); }} data-testid="invoice-mode-mensile">Mensile</Button>
                  <Button size="sm" variant={invoiceMode === 'annuale' ? 'default' : 'outline'} onClick={() => { setInvoiceMode('annuale'); const y = Object.keys(invoiceData.yearly_totals).sort(); if (y.length > 0) setInvoicePeriod(y[y.length - 1]); }} data-testid="invoice-mode-annuale">Annuale</Button>
                </div>
                <select className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm" value={invoicePeriod} onChange={(e) => setInvoicePeriod(e.target.value)} data-testid="invoice-period-select">
                  {invoiceMode === 'mensile' ? Object.keys(invoiceData.monthly_totals).sort().map(m => <option key={m} value={m}>{m}</option>) : Object.keys(invoiceData.yearly_totals).sort().map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div className="p-3 rounded-lg bg-muted text-sm space-y-1">
                <p><strong>Nome:</strong> {invoiceData.user.nome_cognome}</p>
                {invoiceData.user.societa_associazione && <p><strong>Societ\u00e0:</strong> {invoiceData.user.societa_associazione}</p>}
                {invoiceData.user.indirizzo && <p><strong>Indirizzo:</strong> {invoiceData.user.indirizzo}</p>}
                {invoiceData.user.codice_fiscale && <p><strong>Cod. Fiscale:</strong> {invoiceData.user.codice_fiscale}</p>}
                {invoiceData.user.partita_iva && <p><strong>P.IVA:</strong> {invoiceData.user.partita_iva}</p>}
                {invoiceData.user.codice_fattura_digitale && <p><strong>Cod. SDI:</strong> {invoiceData.user.codice_fattura_digitale}</p>}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead><tr className="border-b"><th className="text-left p-2">Sala</th><th className="text-left p-2">Periodo</th><th className="text-left p-2">Descrizione</th><th className="text-right p-2">Totale</th></tr></thead>
                  <tbody>
                    {getFilteredBookings().map((b, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-2">{b.hall_name}</td><td className="p-2 text-xs">{b.start_datetime?.substring(0,16)} - {b.end_datetime?.substring(0,16)}</td><td className="p-2">{b.descrizione_manifestazione}</td><td className="p-2 text-right font-medium">&euro;{(b.total_price||0).toFixed(2)}</td>
                      </tr>
                    ))}
                    {getFilteredBookings().length === 0 && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">Nessuna prenotazione per questo periodo</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-primary/10 border-2 border-primary">
                <span className="font-bold text-lg">TOTALE {invoiceMode === 'mensile' ? 'MENSILE' : 'ANNUALE'} ({invoicePeriod}):</span>
                <span className="font-bold text-2xl text-primary">&euro;{getFilteredTotal().toFixed(2)}</span>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => { navigator.clipboard.writeText(generateInvoiceText()); toast.success('Fattura copiata!'); }} className="flex-1">Copia Testo Fattura</Button>
                <Button variant="outline" onClick={() => { const text = generateInvoiceText(); const subject = `Fattura ${invoiceMode} ${invoicePeriod} - ${invoiceData.user.nome_cognome}`; window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`; }} className="flex-1"><Mail className="w-4 h-4 mr-2" /> Invia via Email</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
